import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Link as LinkIcon, Link2Off, Loader2, ClipboardPaste,
         RefreshCw, ExternalLink, ShieldCheck, AlertTriangle,
         CalendarClock, ScrollText, Bell, Send, XCircle, CheckCircle2,
         Sparkles, Repeat } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

const iso = (s) => s ? new Date(s).toLocaleString() : "—";

/**
 * Vinci ⇄ Zoho Contracts admin console.
 *  - OAuth handshake (connect, paste-code fallback, disconnect)
 *  - Summary tiles (Active · Expiring 30d · Expired)
 *  - Ledger table (partner, type, effective → expiry, status)
 *  - Manual "sweep Sign → Contracts" and "send expiry digest"
 */
export function AdminLegalDeskPanel({ api }) {
  const [status, setStatus] = useState(null);
  const [sum, setSum] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [pasteCode, setPasteCode] = useState("");
  const [days, setDays] = useState(30);
  const [renewTarget, setRenewTarget] = useState(null);
  const [renewMonths, setRenewMonths] = useState(12);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: s }, sumResp] = await Promise.all([
        api.get("/admin/zoho-contracts/status"),
        api.get(`/admin/zoho-contracts/summary?days=${days}`).catch(() => ({ data: null })),
      ]);
      setStatus(s);
      setSum(sumResp?.data || null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load Zoho Contracts");
    } finally { setLoading(false); }
  }, [api, days]);

  useEffect(() => { load(); }, [load]);

  const connect = async () => {
    setBusy("connect");
    try {
      const { data } = await api.get("/admin/zoho-contracts/connect");
      window.location.href = data.authorize_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cannot start connect");
    } finally { setBusy(""); }
  };
  const exchange = async () => {
    if (!pasteCode.trim()) return;
    setBusy("exchange");
    try {
      await api.post("/admin/zoho-contracts/exchange-code", { code: pasteCode.trim() });
      toast.success("Zoho Contracts connected");
      setPasteCode(""); await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Code exchange failed");
    } finally { setBusy(""); }
  };
  const disconnect = async () => {
    if (!window.confirm("Disconnect Zoho Contracts? Sign → Contracts sync will pause.")) return;
    setBusy("disconnect");
    try {
      await api.post("/admin/zoho-contracts/disconnect", {});
      toast.info("Zoho Contracts disconnected"); await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Disconnect failed"); }
    finally { setBusy(""); }
  };
  const sync = async () => {
    setBusy("sync");
    try {
      const { data } = await api.post("/admin/zoho-contracts/sync", {});
      toast.success(`Sweep done · ${data.created} new · ${data.skipped} mirrored · ${data.errored} errored`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sweep failed");
    } finally { setBusy(""); }
  };
  const sendDigest = async () => {
    setBusy("digest");
    try {
      const { data } = await api.post("/admin/zoho-contracts/send-digest", {});
      if (data.sent) toast.success(`Digest sent to ${(data.recipients || []).join(", ")}`);
      else toast.info(data.reason || "Nothing to send today");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Digest failed");
    } finally { setBusy(""); }
  };
  const updateStatus = async (row, next) => {
    try {
      await api.post(`/admin/zoho-contracts/${row.zoho_sign_request_id}/status`,
                       { status: next });
      toast.success(`Contract → ${next}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };

  const confirmRenewal = async () => {
    if (!renewTarget) return;
    setBusy("renew");
    try {
      const { data } = await api.post(
        `/admin/zoho-contracts/${renewTarget.zoho_sign_request_id}/renew`,
        { term_months: renewMonths });
      toast.success(
        `Renewal envelope sent · new expiry ${data.renewal_expiry} · signer ${data.signer_email}`);
      setRenewTarget(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Renewal failed");
    } finally { setBusy(""); }
  };

  if (loading && !status) {
    return <div className="py-16 flex justify-center" data-testid="admin-legal-loading">
      <Loader2 className="w-6 h-6 text-[#B87333] animate-spin" /></div>;
  }
  const connected = status?.connected;
  const configured = status?.configured;

  return (
    <div className="space-y-6" data-testid="admin-legal-panel">
      <div className="border border-white/10 bg-black/40 p-6">
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div>
            <div className="font-grotesk text-[10px] uppercase tracking-[0.32em] text-[#8A6A1A] mb-2">Vinci Legal Desk</div>
            <h2 className="font-serif text-2xl text-[#F5F5F0]">Zoho Contracts · Lifecycle Pulse</h2>
            <p className="text-sm text-[#C9C9C2] mt-2 max-w-2xl">
              Every partner MSA / Rate Contract / Onboarding Agreement signed via Zoho Sign is auto-mirrored into Zoho Contracts, with the signed PDF attached and a <span className="text-[#F6E4A6]">{status?.default_term_months || 12}-month</span> term applied by default. Expiry digest sends at 09:00 IST on <span className="text-[#F6E4A6]">T-30 / T-15 / T-1</span>.
            </p>
          </div>
          <div>
            {connected ? (
              <span className="inline-flex items-center gap-2 text-[#2FB999] font-grotesk text-[10px] uppercase tracking-[0.28em] px-3 py-1.5 border border-[#2FB999]/40">
                <CheckCircle2 className="w-3.5 h-3.5" /> Connected
              </span>
            ) : (
              <span className="inline-flex items-center gap-2 text-[#C0392B] font-grotesk text-[10px] uppercase tracking-[0.28em] px-3 py-1.5 border border-[#C0392B]/40">
                <XCircle className="w-3.5 h-3.5" /> Not connected
              </span>
            )}
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-4 mt-6">
          <StatusRow label="Configured" value={configured ? "Yes" : "No — set env keys"} />
          <StatusRow label="API base" value={status?.api_base || "—"} mono />
          <StatusRow label="Connected at" value={iso(status?.connected_at)} />
        </div>
        {!configured && (
          <div className="mt-4 border border-[#D8B23E]/30 bg-[#D8B23E]/5 px-4 py-3 text-xs text-[#F6E4A6]">
            Set <span className="font-mono">ZOHO_CONTRACTS_CLIENT_ID</span> and
            <span className="font-mono"> ZOHO_CONTRACTS_CLIENT_SECRET</span> in <span className="font-mono">/app/backend/.env</span>.
          </div>
        )}

        <div className="mt-6 flex flex-wrap gap-3">
          {!connected ? (
            <>
              <Button data-testid="legal-connect-btn" onClick={connect} disabled={!configured || busy}>
                {busy === "connect" ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <LinkIcon className="w-4 h-4 mr-2" />}
                Connect Zoho Contracts
              </Button>
              <div className="flex-1 flex items-center gap-2 min-w-[280px]">
                <Input data-testid="legal-paste-code" value={pasteCode}
                        onChange={(e) => setPasteCode(e.target.value)}
                        placeholder="Or paste a self-client code…" />
                <Button data-testid="legal-exchange-btn"
                        variant="secondary" onClick={exchange}
                        disabled={!pasteCode.trim() || busy}>
                  {busy === "exchange" ? <Loader2 className="w-4 h-4 animate-spin" /> : <ClipboardPaste className="w-4 h-4" />}
                </Button>
              </div>
            </>
          ) : (
            <>
              <Button data-testid="legal-sync-btn" variant="outline" onClick={sync} disabled={busy}>
                {busy === "sync" ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                Sync Sign → Contracts
              </Button>
              <Button data-testid="legal-digest-btn" variant="outline" onClick={sendDigest} disabled={busy}>
                {busy === "digest" ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                Send digest now
              </Button>
              <a href={(status?.api_base || "").replace("/api/v1", "")}
                  target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 border border-white/10 hover:border-[#B87333] font-grotesk text-xs uppercase tracking-[0.2em] transition-colors">
                <ExternalLink className="w-3.5 h-3.5" /> Open Zoho Contracts
              </a>
              <Button data-testid="legal-disconnect-btn" variant="destructive"
                      onClick={disconnect} disabled={busy}>
                {busy === "disconnect" ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Link2Off className="w-4 h-4 mr-2" />}
                Disconnect
              </Button>
            </>
          )}
        </div>
      </div>

      {/* SUMMARY TILES */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Tile icon={ShieldCheck} label="Total mirrored" value={sum?.total ?? "—"} testid="legal-stat-total" />
        <Tile icon={CheckCircle2} label="Active" value={sum?.active ?? "—"} tone="ok" testid="legal-stat-active" />
        <Tile icon={CalendarClock} label={`Expiring ≤ ${days}d`} value={sum?.expiring_soon ?? "—"} tone="warn" testid="legal-stat-expiring" />
        <Tile icon={AlertTriangle} label="Expired" value={sum?.expired ?? "—"} tone="danger" testid="legal-stat-expired" />
      </div>

      {/* LEDGER */}
      <div className="border border-white/10 bg-black/40 p-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div>
            <div className="font-grotesk text-[10px] uppercase tracking-[0.32em] text-[#8A6A1A] mb-1">Ledger</div>
            <h3 className="font-serif text-xl text-[#F5F5F0] flex items-center gap-2">
              <ScrollText className="w-4 h-4 text-[#B87333]" /> Partner contracts
            </h3>
          </div>
          <div className="flex items-center gap-2">
            {[30, 60, 90].map(n => (
              <button key={n} data-testid={`legal-days-${n}`} onClick={() => setDays(n)}
                       className={`px-3 py-1.5 font-grotesk text-[10px] uppercase tracking-[0.28em] transition-colors ${days === n ? "bg-[#F5F5F0] text-black" : "border border-white/15 text-[#C9C9C2] hover:border-[#B87333]"}`}>
                ≤ {n}d
              </button>
            ))}
          </div>
        </div>
        {(sum?.rows || []).length === 0 ? (
          <div className="py-10 text-center text-[#8A8A82] font-body text-sm border border-dashed border-white/10">
            No contracts mirrored yet. Once a partner completes eKYC + Zoho Sign, the record lands here within 30 minutes.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="legal-ledger-table">
              <thead>
                <tr className="text-[#8A6A1A] font-grotesk text-[10px] uppercase tracking-[0.28em] border-b border-white/10">
                  <th className="text-left py-3">Partner</th>
                  <th className="text-left py-3">Type</th>
                  <th className="text-left py-3">Effective</th>
                  <th className="text-left py-3">Expiry</th>
                  <th className="text-left py-3">Status</th>
                  <th className="text-left py-3">Actions</th>
                  <th className="text-left py-3">Renew</th>
                </tr>
              </thead>
              <tbody className="text-[#C9C9C2] font-body">
                {(sum.rows || []).map((r) => (
                  <tr key={r.zoho_sign_request_id} className="border-b border-white/5">
                    <td className="py-3">
                      <div className="text-[#F5F5F0]">{r.partner_name || "—"}</div>
                      <div className="text-[#8A8A82] text-xs mt-0.5">{r.partner_email}</div>
                    </td>
                    <td className="py-3">{r.contract_type}</td>
                    <td className="py-3 text-xs">{r.effective_date}</td>
                    <td className="py-3 text-xs">
                      {r.expiry_date}
                      {typeof r.days_to_expiry === "number" && (
                        <span className={`ml-2 text-[10px] ${r.days_to_expiry < 0 ? "text-[#C0392B]" : r.days_to_expiry <= 30 ? "text-[#D8B23E]" : "text-[#8A8A82]"}`}>
                          ({r.days_to_expiry < 0 ? `${-r.days_to_expiry}d overdue` : `${r.days_to_expiry}d`})
                        </span>
                      )}
                    </td>
                    <td className="py-3">
                      <StatusPill status={r.status} />
                    </td>
                    <td className="py-3">
                      <select
                        data-testid={`legal-status-select-${r.zoho_sign_request_id}`}
                        defaultValue=""
                        onChange={(e) => e.target.value && updateStatus(r, e.target.value)}
                        className="bg-transparent border border-white/15 text-xs px-2 py-1 hover:border-[#B87333] transition-colors"
                      >
                        <option value="" className="bg-black">…</option>
                        <option value="Active" className="bg-black">Active</option>
                        <option value="Renewed" className="bg-black">Renewed</option>
                        <option value="Terminated" className="bg-black">Terminated</option>
                        <option value="Expired" className="bg-black">Expired</option>
                      </select>
                    </td>
                    <td className="py-3">
                      {["Active", "Expired"].includes(r.status) ? (
                        <button
                          data-testid={`legal-renew-btn-${r.zoho_sign_request_id}`}
                          onClick={() => { setRenewTarget(r); setRenewMonths(12); }}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#D8B23E]/60 text-[#F6E4A6] font-grotesk text-[10px] uppercase tracking-[0.24em] hover:bg-[#D8B23E] hover:text-black transition-colors"
                        >
                          <Repeat className="w-3 h-3" /> Renew 12mo
                        </button>
                      ) : r.status === "Renewal Pending" ? (
                        <span className="text-[#D8B23E] font-grotesk text-[10px] uppercase tracking-[0.24em]">Awaiting sign</span>
                      ) : r.status === "Awaiting Signature" ? (
                        <span className="text-[#8A8A82] font-grotesk text-[10px] uppercase tracking-[0.24em]">Child env</span>
                      ) : (
                        <span className="text-[#8A8A82] text-xs">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {renewTarget && (
        <RenewalModal target={renewTarget} months={renewMonths}
                        setMonths={setRenewMonths} busy={busy}
                        onClose={() => setRenewTarget(null)}
                        onConfirm={confirmRenewal} />
      )}
    </div>
  );
}


function RenewalModal({ target, months, setMonths, busy, onClose, onConfirm }) {
  return (
    <div className="fixed inset-0 z-[70] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
         data-testid="legal-renew-modal"
         onClick={onClose}>
      <div className="max-w-md w-full bg-[#0A0906] border border-[#D8B23E]/40"
           onClick={(e) => e.stopPropagation()}>
        <div className="h-1 bg-gradient-to-r from-[#D8B23E] via-[#F6E4A6] to-[#B87333]" />
        <div className="p-8">
          <div className="flex items-center gap-2 text-[#8A6A1A] font-grotesk text-[10px] uppercase tracking-[0.28em] mb-4">
            <Sparkles className="w-3.5 h-3.5" /> Renewal Wizard
          </div>
          <h3 className="font-serif text-2xl text-[#F5F5F0] leading-tight">
            Renew {target.contract_type}?
          </h3>
          <p className="mt-3 text-sm text-[#C9C9C2]">
            A fresh Zoho Sign envelope will be dispatched to <span className="text-[#F6E4A6]">{target.partner_email || target.partner_name}</span>.
            Once they sign, the Zoho Contracts record will roll forward
            automatically and the ledger will move this contract to <span className="text-[#2FB999]">Renewed</span>.
          </p>
          <div className="mt-6">
            <label className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#8A6A1A] block mb-2">
              Renewal term (months)
            </label>
            <div className="flex gap-2">
              {[6, 12, 24, 36].map(m => (
                <button
                  key={m}
                  data-testid={`legal-renew-months-${m}`}
                  onClick={() => setMonths(m)}
                  className={`flex-1 py-2 font-grotesk text-xs uppercase tracking-[0.24em] transition-colors ${months === m ? "bg-[#F5F5F0] text-black" : "border border-white/15 text-[#C9C9C2] hover:border-[#B87333]"}`}
                >{m}m</button>
              ))}
            </div>
          </div>
          <div className="mt-8 flex justify-end gap-3">
            <button
              data-testid="legal-renew-cancel"
              onClick={onClose}
              className="px-4 py-2 border border-white/15 font-grotesk text-xs uppercase tracking-[0.24em] hover:border-white/40 transition-colors"
            >Cancel</button>
            <button
              data-testid="legal-renew-confirm"
              onClick={onConfirm}
              disabled={busy === "renew"}
              className="px-6 py-2 bg-gradient-to-r from-[#D8B23E] via-[#F6E4A6] to-[#B87333] text-black font-grotesk text-xs uppercase tracking-[0.24em] hover:shadow-[0_0_24px_rgba(216,178,62,0.4)] transition-shadow disabled:opacity-60 inline-flex items-center gap-2"
            >
              {busy === "renew" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Repeat className="w-3.5 h-3.5" />}
              Send renewal envelope
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


function StatusRow({ label, value, mono }) {
  return (
    <div className="border-l border-[#B87333]/30 pl-3">
      <div className="font-grotesk text-[9px] uppercase tracking-[0.28em] text-[#8A6A1A]">{label}</div>
      <div className={`mt-1 text-sm text-[#F5F5F0] break-all ${mono ? "font-mono text-xs" : ""}`}>{value || "—"}</div>
    </div>
  );
}
function Tile({ icon: Icon, label, value, tone, testid }) {
  const color = tone === "danger" ? "text-[#C0392B]"
                : tone === "warn" ? "text-[#D8B23E]"
                : tone === "ok" ? "text-[#2FB999]"
                : "text-[#F5F5F0]";
  return (
    <div className="border border-white/10 bg-black/20 p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-[#8A6A1A] mb-3">
        <Icon className="w-4 h-4" />
        <span className="font-grotesk text-[9px] uppercase tracking-[0.28em]">{label}</span>
      </div>
      <div className={`font-serif text-3xl ${color}`}>{value}</div>
    </div>
  );
}
function StatusPill({ status }) {
  const map = {
    Active: ["#2FB999", "#2FB99930"],
    Expired: ["#C0392B", "#C0392B30"],
    Terminated: ["#8A8A82", "#8A8A8230"],
    Draft: ["#D8B23E", "#D8B23E30"],
    Renewed: ["#F6E4A6", "#F6E4A630"],
  };
  const [fg, bg] = map[status] || ["#C9C9C2", "#C9C9C230"];
  return <span className="font-grotesk text-[9px] uppercase tracking-[0.24em] px-2 py-1 rounded-sm"
                 style={{ color: fg, background: bg, border: `1px solid ${fg}55` }}>{status || "—"}</span>;
}
